import {useQueryClient} from "@tanstack/react-query";
import React, {useEffect, useRef, useState} from 'react';
import {ReferenceDefinitionDto, ReferenceDefinitionUpdateDto, RelationshipDirection} from "../../../../../common/apis/stereotype/ReferenceDefinitionDto";
import {StereotypeApiClient} from "../../../../../common/apis/stereotype/StereotypeApiClient";
import {ArchimateRelationship} from "../../../../../common/archimate/ArchimateRelationship";
import {ValidationError} from "../../../../../common/ValidationError";
import {ButtonLayout, NegativeButtons, PositiveButtons} from "../../../../../components/button/ButtonLayout";
import {CancelButton} from "../../../../../components/button/CancelButton";
import {SaveButton} from "../../../../../components/button/SaveButton";
import Dialog from '../../../../../components/dialogs/Dialog';
import DialogActions from "../../../../../components/dialogs/DialogActions";
import DialogContent from '../../../../../components/dialogs/DialogContent';
import DialogTitle from '../../../../../components/dialogs/DialogTitle';
import Grid from '../../../../../components/dialogs/Grid';
import Checkbox from "../../../../../components/fields/Checkbox";
import SingleSelectField from "../../../../../components/fields/select/SingleSelectField";
import TextField from '../../../../../components/fields/textfield/TextField';
import {t} from '../../../../../store/localization/TranslMessasge';
import {CommonTranslation} from '../../CommonTranslation';
import {ErrorTranslationKey} from "../../ErrorTranslationKey";
import Snackbar from "../../snackbar/Snackbar";
import {StereotypeErrorCode} from "../StereotypeErrorCodes";
import {invalidateFindAllStereotypesQueryCache} from "../StereotypeQuery";
import {StereotypesTranslationKey} from "../StereotypesTranslationKey";
import {ReferenceRelationshipDirectionFormatter} from "./ReferenceRelationshipDirectionFormatter";

interface ReferenceDefinitionUpdateDialogProps {
    open: boolean;
    stereotypeId: string;
    referenceDefinition: ReferenceDefinitionDto;
    onUpdated: (referenceDefinitionId: string) => void;
    onClose: () => void;
}

export function ReferenceDefinitionUpdateDialog({ open, stereotypeId, referenceDefinition, onUpdated, onClose }: Readonly<ReferenceDefinitionUpdateDialogProps>) {
    const stereotypeApiClient = useRef(new StereotypeApiClient());

    const [referenceDefUpdate, setReferenceDefUpdate] = useState<Partial<ReferenceDefinitionUpdateDto>>({});
    const [relationshipType, setRelationshipType] = useState<ArchimateRelationship | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [inProgress, setInProgress] = useState(false);

    const queryClient = useQueryClient();

    useEffect(() => {
        if (open) {
            setReferenceDefUpdate({
                name: referenceDefinition.name,
                description: referenceDefinition.description,
                relationshipType: referenceDefinition.relationshipType,
                relationshipDirection: referenceDefinition.relationshipDirection,
                required: referenceDefinition.required,
                multiple: referenceDefinition.multiple,
            });
            
            const relationship = ArchimateRelationship.findByStandardName(referenceDefinition.relationshipType);
            setRelationshipType(relationship);
            
            setErrors({});
        }
    }, [open, referenceDefinition]);

    useEffect(() => {
        if (relationshipType) {
            setReferenceDefUpdate(prev => ({
                ...prev,
                relationshipType: relationshipType.standardNames[0]
            }));
        }
    }, [relationshipType]);

    function updateReferenceDefinition() {
        if (!validateForm()) {
            return;
        }

        setInProgress(true);

        const referenceDefUpdateDto: ReferenceDefinitionUpdateDto = {
            name: referenceDefUpdate.name!.trim(),
            description: referenceDefUpdate.description?.trim(),
            relationshipType: referenceDefUpdate.relationshipType!,
            relationshipDirection: referenceDefUpdate.relationshipDirection!,
            required: referenceDefUpdate.required ?? false,
            multiple: referenceDefUpdate.multiple ?? false,
        };

        stereotypeApiClient.current.updateReference(stereotypeId, referenceDefinition.id, referenceDefUpdateDto)
                .then(() => {
                    onUpdated(referenceDefinition.id);
                    onClose();
                    invalidateFindAllStereotypesQueryCache(queryClient);
                    Snackbar.success(t(CommonTranslation.DATA_SAVED_SUCESSFULLY));
                })
                .catch((error) => {
                    if (error instanceof ValidationError && error.error.code === StereotypeErrorCode.CONFLICTING_NAME) {
                        setErrors({name: t(StereotypesTranslationKey.ATTRIBUTE_WITH_SAME_NAME_ALREADY_EXISTS)});
                    } else {
                        Snackbar.error(t(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED), error);
                    }
                })
                .finally(() => setInProgress(false));
    }

    function validateForm(): boolean {
        const newErrors: Record<string, string> = {};
        if (!referenceDefUpdate.name?.trim()) {
            newErrors.name = t(CommonTranslation.FILL_OUT_THIS_FIELD);
        }

        if (!referenceDefUpdate.relationshipType) {
            newErrors.relationshipType = t(CommonTranslation.FILL_OUT_THIS_FIELD);
        }

        if (!referenceDefUpdate.relationshipDirection) {
            newErrors.relationshipDirection = t(CommonTranslation.FILL_OUT_THIS_FIELD);
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    const directionOptions = Object.keys(RelationshipDirection) as RelationshipDirection[];
    const relationshipTypeOptions = ArchimateRelationship.values();

    return (
            <Dialog open={open} onClose={onClose} maxWidth="md">
                <DialogTitle title={t(StereotypesTranslationKey.EDIT_SMART_REFERENCE)}
                        onDialogClosed={onClose} />
                <DialogContent>
                    <Grid container>
                        <Grid item xs={12} sm={12}>
                            <TextField
                                    label={t(StereotypesTranslationKey.NAME)}
                                    value={referenceDefUpdate.name}
                                    onChange={value => setReferenceDefUpdate({...referenceDefUpdate, name: value})}
                                    errorMessage={errors.name}
                                    required={true}/>
                        </Grid>
                        <Grid item xs={12} sm={12} md={5}>
                            <SingleSelectField
                                    label={t(StereotypesTranslationKey.RELATIONSHIP_TYPE)}
                                    value={relationshipType}
                                    onChange={value => setRelationshipType(value as ArchimateRelationship | null)}
                                    options={relationshipTypeOptions}
                                    getOptionLabel={(option) => option.visibleName}
                                    errorMessage={errors.relationshipType}
                                    required={true}/>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <SingleSelectField
                                    label={t(StereotypesTranslationKey.RELATIONSHIP_DIRECTION)}
                                    value={referenceDefUpdate.relationshipDirection}
                                    onChange={value => setReferenceDefUpdate({...referenceDefUpdate, relationshipDirection: value as RelationshipDirection})}
                                    options={directionOptions}
                                    getOptionLabel={(option) => ReferenceRelationshipDirectionFormatter.formatDirection(option, t)}
                                    errorMessage={errors.relationshipDirection}
                                    required={true}/>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Checkbox
                                    label={t(StereotypesTranslationKey.MULTIPLE)}
                                    checked={referenceDefUpdate.multiple}
                                    onChange={value => setReferenceDefUpdate({...referenceDefUpdate, multiple: value})}/>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                    label={t(CommonTranslation.DESCRIPTION)}
                                    value={referenceDefUpdate.description ?? ''}
                                    onChange={value => setReferenceDefUpdate({...referenceDefUpdate, description: value})}
                                    multiline
                                    rows={4}/>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Checkbox
                                    label={t(StereotypesTranslationKey.SMART_REFERENCE_REQUIRED)}
                                    checked={referenceDefUpdate.required}
                                    onChange={value => setReferenceDefUpdate({...referenceDefUpdate, required: value})}/>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <ButtonLayout>
                        <PositiveButtons>
                            <SaveButton onClick={updateReferenceDefinition} disabled={inProgress} />
                        </PositiveButtons>
                        <NegativeButtons>
                            <CancelButton onClick={onClose} disabled={inProgress} />
                        </NegativeButtons>
                    </ButtonLayout>
                </DialogActions>
            </Dialog>
    );
}